import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { competitiveClosing, competitiveClosingNote, lifecycle, marketCategories, vexuDifferentiators, vexuSpan } from '../../data/deckSlide9Competitive';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
type Stage = 0 | 1 | 2 | 3 | 4 | 5;
const CAT_LEFT = 112;
const CAT_W = 316;
const CAT_GAP = 20;
export function CompetitiveSlide() {
  const [stage, setStage] = useState<Stage>(0);
  useEffect(() => {
    const timers: number[] = [];
    const at = (ms: number, next: Stage) => timers.push(window.setTimeout(() => setStage(next), ms));
    at(700, 1); // fragmented categories
    at(1700, 2); // representative players
    at(2900, 3); // VEXU spanning the layers
    at(4400, 4); // lifecycle
    at(5800, 5); // closing
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const shown = (s: Stage) => stage >= s;
  return <SlideStage data-id="element-2178" data-mp="1a4s8eiyw7oy6">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2179" data-mp="1a5a3vddqdz51">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 105%, rgba(0,200,83,0.08) 0%, rgba(250,252,249,0) 58%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2180" data-mp="wdvfss4yv7r4" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2181" data-mp="rflm6lyw4f7m">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2182" data-mp="ifyly3hwdfg5" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2183" data-mp="27tbt00he9hky">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2184" data-mp="2ad8fxa2xyb7y">
          09 · Competitive Positioning
        </motion.span>

        {/* Headline */}
        <motion.h1 className="absolute text-[38px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 122,
        width: 1000
      }} initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.9,
        delay: 0.15,
        ease
      }} data-id="element-2185" data-mp="1wfa68hzkf2ys">
          A fragmented market. One integrated communication layer.
        </motion.h1>
        <motion.p className="absolute text-[15.5px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 232,
        width: 900
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 0.6,
        ease
      }} data-id="element-2186" data-mp="3crp5rmpf0fg9">
          The market is solving individual pieces of AI communication. VEXU
          connects the consumer, caller and business experience through one
          underlying AI engine.
        </motion.p>

        {/* Fragmented categories */}
        {marketCategories.map((cat, i) => <motion.div key={cat.category} className="absolute border-t border-[#0B1712]/[0.1] pt-4" style={{
        left: CAT_LEFT + i * (CAT_W + CAT_GAP),
        top: 312,
        width: CAT_W
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(1) ? 1 : 0,
        y: shown(1) ? 0 : 10
      }} transition={{
        duration: 0.8,
        delay: i * 0.12,
        ease
      }} data-id="element-2187" data-mp="3uapbzo9yf58k" data-mpi={i}>
            <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[#5A6B60]" data-id="element-2188" data-mp="ludrtrqmsd9k">
              {cat.category}
            </p>
            <motion.div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5" initial={{
          opacity: 0
        }} animate={{
          opacity: shown(2) ? 1 : 0
        }} transition={{
          duration: 0.7,
          delay: i * 0.1,
          ease
        }} data-id="element-2189" data-mp="pt3d06hwuz3r">
              {cat.players.map(player => <span key={player} className="text-[15px] font-medium text-[#33463B]" data-id="element-2190" data-mp="1we72y2ocdrvn">
                  {player}
                </span>)}
            </motion.div>
            <p className="mt-3 text-[11.5px] leading-snug text-[#A3AFA7]" data-id="element-2191" data-mp="zmmzghhyt8eo">
              {cat.descriptor}
            </p>
          </motion.div>)}

        {/* VEXU — the unified layer beneath the fragmented categories */}
        <motion.div className="absolute rounded-3xl" style={{
        left: CAT_LEFT - 28,
        top: 452,
        width: 3 * (CAT_W + CAT_GAP) + CAT_W + 56,
        height: 206,
        backgroundColor: 'rgba(0,200,83,0.055)',
        border: '1px solid rgba(0,200,83,0.18)'
      }} initial={{
        opacity: 0,
        scaleY: 0.96
      }} animate={{
        opacity: shown(3) ? 1 : 0,
        scaleY: shown(3) ? 1 : 0.96
      }} transition={{
        duration: 1,
        ease
      }} data-id="element-2192" data-mp="18m0f5rwomt7u" />

        <motion.div className="absolute" style={{
        left: CAT_LEFT,
        top: 486,
        width: 440
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: shown(3) ? 1 : 0,
        y: shown(3) ? 0 : 10
      }} transition={{
        duration: 0.9,
        delay: 0.2,
        ease
      }} data-id="element-2193" data-mp="2njmgtskpofin">
          <div className="flex items-center gap-3" data-id="element-2194" data-mp="31jnktgw9w32c">
            <img src={VEXU_LOGO} alt="" className="h-6 w-6 object-contain" data-id="element-2195" data-mp="31j4aal8ycgyc" />
            <span className="text-[26px] font-semibold tracking-[-0.02em] text-[#0B1712]" data-id="element-2196" data-mp="36hozzqqkvi8j">
              VEXU
            </span>
            <span className="rounded-full border border-vexu/25 bg-vexu/[0.07] px-2.5 py-[3px] text-[9.5px] font-semibold uppercase tracking-[0.18em] text-vexu-700" data-id="element-2197" data-mp="3u7051keynysh">
              Patent pending
            </span>
          </div>
          <p className="mt-2 text-[14px] font-medium text-vexu-700" data-id="element-2198" data-mp="7qjatnum2516">
            AI communication layer
          </p>
          <p className="mt-3.5 text-[12px] leading-snug text-[#7C8B82]" data-id="element-2199" data-mp="2r6g6gxhzto1">
            {vexuSpan.join('  ·  ')}
          </p>
        </motion.div>

        {/* Differentiators */}
        <div className="absolute grid grid-cols-2 gap-x-12 gap-y-4" style={{
        left: 616,
        top: 486,
        width: 872
      }} data-id="element-2200" data-mp="3uxl9jqqilzlc">
          {vexuDifferentiators.map((item, i) => <motion.div key={item.title} className="flex items-start gap-3" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: shown(3) ? 1 : 0,
          y: shown(3) ? 0 : 8
        }} transition={{
          duration: 0.7,
          delay: 0.3 + i * 0.1,
          ease
        }} data-id="element-2201" data-mp="3l3mdn9h1bc1u" data-mpi={i}>
              <span className="mt-[7px] shrink-0 rounded-full" style={{
            width: 4,
            height: 4,
            backgroundColor: 'rgba(0,153,64,0.5)'
          }} data-id="element-2202" data-mp="326l3zbcjcnrk" />
              <span data-id="element-2203" data-mp="1mm2eoib8wtwp">
                <span className="block text-[13.5px] font-semibold text-[#0B1712]" data-id="element-2204" data-mp="1mpqa5n093qe2">
                  {item.title}
                </span>
                <span className="mt-1 block text-[12px] leading-snug text-[#7C8B82]" data-id="element-2205" data-mp="1votueub5mbu5">
                  {item.detail}
                </span>
              </span>
            </motion.div>)}
        </div>

        {/* Lifecycle */}
        <motion.div className="absolute flex items-center gap-3 border-t border-[#0B1712]/[0.09] pt-6" style={{
        left: 112,
        right: 112,
        bottom: 118
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(4) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2206" data-mp="13062wgztaq85">
          {lifecycle.map((step, i) => <React.Fragment key={step} data-id="element-2207" data-mp="k8vfi9taovl0" data-mpi={i}>
              {i > 0 && <span className="text-[12px] text-[#C3CCC5]" data-id="element-2208" data-mp="k7k8800n55p5">→</span>}
              <motion.span className="text-[16px] font-semibold uppercase tracking-[0.1em] text-vexu-700" initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: shown(4) ? 1 : 0,
            y: shown(4) ? 0 : 6
          }} transition={{
            duration: 0.5,
            delay: i * 0.12,
            ease
          }} data-id="element-2209" data-mp="39qf83ouayp3p">
                {step}
              </motion.span>
            </React.Fragment>)}
        </motion.div>

        {/* Closing */}
        <motion.div className="absolute" style={{
        left: 112,
        right: 112,
        bottom: 48
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: shown(5) ? 1 : 0
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2210" data-mp="3nqzo6d8u26ti">
          <p className="text-[19px] font-semibold tracking-[-0.015em] text-[#0B1712]" data-id="element-2211" data-mp="1cq1c8kjx6uu">
            {competitiveClosing}
          </p>
          <p className="mt-2 text-[13px] text-[#8A998F]" data-id="element-2212" data-mp="f8ts1rw7ux2k">
            {competitiveClosingNote}
          </p>
        </motion.div>
      </div>
    </SlideStage>;
}